import { linkPayload } from './assign.ts';
import type { Staged } from './assign';

/**
 * Every operator change the room dialog is holding but has not written yet.
 *
 * <p>
 * <b>Renames and removals are keyed by ASSIGNMENT id, and staged people have none.</b> That is the
 * shape of the thing rather than a convenience: a link is what carries the name, so somebody
 * operating two rooms has two rows and two names, and there is no path here that widens to "this
 * person". A staged person is not a link yet, so their name lives on the staging entry until the
 * one all-or-nothing request that creates it.
 * </p>
 */
export type PendingOperators = {
  /** People to link, in the order they were staged. */
  staged: Staged[];
  /** Assignment id to the name this room will know them by. */
  renames: Readonly<Record<string, string>>;
  /** Assignment ids to unlink. */
  removals: readonly string[];
};

/** As much of an assignment as these rules read. Structural, so the DTO passes straight in. */
export type Link = {
  id: string;
  roomId: string;
  userObjectId: string;
  displayName?: string | null;
};

/**
 * One write, in the order it runs.
 *
 * <p>
 * <b>A plan rather than a method that does it all</b>, because the ORDER is the part that can be
 * wrong and the part nothing else would notice. It is also what lets the runner narrow the draft
 * after each success, which is the whole of how a partial failure stays recoverable.
 * </p>
 */
export type SaveStep =
  | { kind: 'create-room' }
  | { kind: 'patch-room' }
  | { kind: 'unlink'; id: string }
  | { kind: 'rename'; id: string; name: string }
  | { kind: 'link'; entries: { objectId: string; name: string }[] };

export const NOTHING_PENDING: PendingOperators = { staged: [], renames: {}, removals: [] };

/** Whether any of this counts as unsaved work. Read by the dialog's single dirty flag. */
export const operatorsDirty = (pending: PendingOperators): boolean =>
  pending.staged.length > 0
  || Object.keys(pending.renames).length > 0
  || pending.removals.length > 0;

export const isRemoved = (pending: PendingOperators, id: string): boolean =>
  pending.removals.includes(id);

/** What the row shows: the pending rename if there is one, otherwise what is stored. */
export const nameOf = (pending: PendingOperators, link: Link): string | null =>
  pending.renames[link.id] ?? link.displayName ?? null;

export const isRenamed = (pending: PendingOperators, link: Link): boolean =>
  Object.hasOwn(pending.renames, link.id);

/**
 * Records a rename, or drops one that has become a no-op.
 *
 * <p>
 * <b>A rename to the name it already has is not a rename.</b> Recorded, it makes the dialog claim
 * unsaved work over nothing and spends a PATCH saying what the row already says — and the ordinary
 * way to reach it is opening the inline editor and pressing the tick without typing. Judged
 * trimmed, because trimmed is what the server stores.
 * </p>
 */
export const withRename = (
  pending: PendingOperators,
  link: Link,
  name: string,
): PendingOperators => {
  const next = name.trim();
  const stored = (link.displayName ?? '').trim();

  const renames = { ...pending.renames };
  if (next === stored) delete renames[link.id];
  else renames[link.id] = next;

  return { ...pending, renames };
};

/**
 * Who this room already has — <b>excluding anybody staged for removal.</b>
 *
 * <p>
 * That exclusion is what makes <em>remove, then re-add under a new name</em> possible in one Save.
 * Without it the search goes on reporting them as already an operator and the second half of the
 * gesture is refused, on a screen where the operator has just undone the first half.
 * </p>
 */
export const linkedObjectIds = (
  links: readonly Link[],
  roomId: string,
  pending: PendingOperators,
): Set<string> =>
  new Set(
    links
      .filter((l) => l.roomId === roomId && !isRemoved(pending, l.id))
      .map((l) => l.userObjectId),
  );

/**
 * What Save does, in order.
 *
 * <p>
 * <b>The room first</b>, and on a create it must be: there is no room id until it answers, and a
 * link naming a room the server cannot find is refused.
 * </p>
 * <p>
 * <b>Removals before additions, and that is the ordering decision with a consequence.</b> The
 * other way round, removing somebody and re-adding them in one Save meets
 * <code>already_assigned</code> — a refusal for something the operator has already undone on the
 * screen in front of them.
 * </p>
 * <p>
 * <b>The one all-or-nothing request goes last.</b> If the link is refused, everything before it has
 * landed and there is the least left to explain; and its own refusal names every offender, so one
 * failure is one message rather than a queue of them.
 * </p>
 */
export const savePlan = (input: {
  creating: boolean;
  roomDirty: boolean;
  pending: PendingOperators;
}): SaveStep[] => {
  const { creating, roomDirty, pending } = input;

  const steps: SaveStep[] = [];

  if (creating) steps.push({ kind: 'create-room' });
  else if (roomDirty) steps.push({ kind: 'patch-room' });

  for (const id of pending.removals) steps.push({ kind: 'unlink', id });

  for (const [id, name] of Object.entries(pending.renames)) {
    // Renaming a row that is about to be unlinked is wasted at best and a 404 at worst, since the
    // unlink above runs first. The screen does not offer it; this refuses to send it either way.
    if (!isRemoved(pending, id)) steps.push({ kind: 'rename', id, name });
  }

  if (pending.staged.length > 0) steps.push({ kind: 'link', entries: linkPayload(pending.staged) });

  return steps;
};

/**
 * What is still pending once a step has been applied.
 *
 * <p>
 * <b>THE NARROWING, and it is the whole of why a partial save is recoverable.</b> Every step that
 * succeeds is dropped from the draft, so pressing Save again retries only what is left — it cannot
 * unlink twice, and it cannot create a second room. A room step leaves this alone: whether the
 * room still needs writing is answered by the draft against what is stored, and by the dialog
 * having become an edit.
 * </p>
 */
export const pendingAfter = (pending: PendingOperators, step: SaveStep): PendingOperators => {
  switch (step.kind) {
    case 'unlink':
      return { ...pending, removals: pending.removals.filter((id) => id !== step.id) };

    case 'rename': {
      const renames = { ...pending.renames };
      delete renames[step.id];
      return { ...pending, renames };
    }

    // One request for the whole staged list, so there is no half of it to keep.
    case 'link':
      return { ...pending, staged: [] };

    default:
      return pending;
  }
};

const and = (items: readonly string[]) =>
  items.length < 2
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * What has already been applied, or null when nothing has.
 *
 * <p>
 * <b>After a partial save, Cancel cannot discard.</b> The room exists, the removals happened, and a
 * footer still offering to throw it all away is promising something it cannot deliver. So the
 * dialog says what landed, relabels the way out, and says that Save will retry only the rest.
 * </p>
 * <p>
 * <b>It claims nothing that did not happen.</b> A note reporting people added, over the link step
 * that was the one to fail, sends an administrator away believing the job is done — which is worse
 * than the failure it is describing.
 * </p>
 */
export const appliedNote = (applied: readonly SaveStep[]): string | null => {
  if (applied.length === 0) return null;

  const parts: string[] = [];

  if (applied.some((s) => s.kind === 'create-room')) parts.push('the room was created');
  if (applied.some((s) => s.kind === 'patch-room')) parts.push("the room's details were saved");

  const unlinked = applied.filter((s) => s.kind === 'unlink').length;
  if (unlinked > 0) parts.push(`${count(unlinked, 'operator', 'operators')} removed`);

  const renamed = applied.filter((s) => s.kind === 'rename').length;
  if (renamed > 0) parts.push(`${count(renamed, 'name', 'names')} changed`);

  const linked = applied
    .filter((s) => s.kind === 'link')
    .reduce((total, s) => total + s.entries.length, 0);
  if (linked > 0) parts.push(`${count(linked, 'operator', 'operators')} added`);

  return `Already saved: ${and(parts)}. Save will retry only what is left.`;
};

/**
 * What deleting the room does to operator changes that have not been saved, or null when there are
 * none.
 *
 * <p>
 * <b>Discarded, never applied.</b> The other reading — that Delete means save-then-destroy — would
 * have the dialog link people to a room in the act of removing it, and every one of those links
 * refused or immediately cascaded away. Said out loud because the two buttons sit in the same
 * footer, and because an operator who had just staged four people would otherwise be entitled to
 * wonder which of the two the dialog thought they meant.
 * </p>
 */
export const discardNote = (pending: PendingOperators): string | null =>
  operatorsDirty(pending)
    ? 'The operator changes you have not saved will be discarded, not applied.'
    : null;

/**
 * What is unsaved in the dialog, named — or null when nothing is.
 *
 * <p>
 * <b>It names WHICH HALF is at stake</b>, because the two are lost together and "unsaved changes"
 * over a dialog holding a room form and an operator list leaves somebody working out what the
 * prompt is about to take.
 * </p>
 * <p>
 * <b>It is also the dirty flag, and REGISTERING A ROOM IS NOT A CHANGE.</b> Folding <em>creating</em>
 * into this made a freshly opened Register dialog dirty, so Cancel asked about discarding work
 * nobody had done. Creating is a fact about which step Save runs — <code>savePlan</code>'s
 * business — and not about whether anything has been typed.
 * </p>
 */
export const unsavedLabel = (input: {
  roomDirty: boolean;
  pending: PendingOperators;
}): string | null => {
  const parts = [
    input.roomDirty ? 'the room’s details' : null,
    operatorsDirty(input.pending) ? 'your operator changes' : null,
  ].filter((p): p is string => p !== null);

  return parts.length === 0 ? null : parts.join(' and ');
};
